import type {
	FiltersInterface,
	SortSelection,
} from '@valk-nx/compositions/ui-filters/src/lib/contracts/filters.interface';
import { SUPPORTED_FILTER_TYPES } from '@valk-nx/compositions/ui-filters/src/lib/contracts/filters.interface';
import type { PriceInterface } from '@valk-nx/core/lib/interfaces/price.interface';
import type {
	FilterableListFilter,
	FilterableMapHotel,
} from '@valk-nx/storyblok-types/src/lib/types/filterable-list.service.interface';

/**
 * Input transform for the CMS-provided `baseUrl`: accepts only a valid
 * https URL and normalizes it to origin + path without trailing slashes.
 * Anything else is rejected and results in an empty string.
 */
export function toBaseUrl(value: string): string {
	try {
		const url = new URL(value);
		if (url.protocol !== 'https:') return '';

		let base = `${url.origin}${url.pathname}`;
		while (base.endsWith('/')) {
			base = base.slice(0, -1);
		}
		return base;
	} catch {
		return '';
	}
}

/**
 * Build an URL for a list item: validated `baseUrl` + slug. Undefined
 * when no `baseUrl` is configured or slug is not present.
 */
export function toPageUrl(baseUrl: string, slug: string): string | undefined {
	return baseUrl && slug ? `${baseUrl}/${slug}` : undefined;
}

export function toCardUrl(hotel: FilterableMapHotel, baseUrl: string): string {
	if (hotel.slug && baseUrl) {
		return toPageUrl(baseUrl, hotel.slug);
	}
	return hotel.toUrl;
}

/** Maps supported BFF filter definitions onto the vp-filters contract. */
export function toFiltersInterface(
	rawFilters: FilterableListFilter[],
): FiltersInterface[] {
	return rawFilters
		.filter((filter) => SUPPORTED_FILTER_TYPES.includes(filter.type))
		.map(({ title, type, data, maxValue }) => ({
			title,
			type,
			maxValue,
			items: (data ?? []).map(({ name, code, frequency, icon }) => ({
				name,
				value: code,
				frequency,
				icon,
			})),
		}));
}

export function sortByOption<T extends { name: string; price: PriceInterface }>(
	items: T[],
	option: SortSelection,
): T[] {
	const sorted = [...items];

	switch (option) {
		case 'name-asc':
			return sorted.sort((a, b) => a.name.localeCompare(b.name));
		case 'name-desc':
			return sorted.sort((a, b) => b.name.localeCompare(a.name));
		case 'price-asc':
			return sorted.sort((a, b) => {
				if (a.price.amount === null && b.price.amount === null)
					return 0;
				if (a.price.amount === null) return 1;
				if (b.price.amount === null) return -1;
				return a.price.amount - b.price.amount;
			});
		case 'price-desc':
			return sorted.sort((a, b) => {
				if (a.price.amount === null && b.price.amount === null)
					return 0;
				if (a.price.amount === null) return 1;
				if (b.price.amount === null) return -1;
				return b.price.amount - a.price.amount;
			});
		default:
			return sorted;
	}
}
